/**
 * Represents the concept of a search.
 */
export default class SearchBase {
  static defaultProps = {
    name: '', // a unique name for this search
    type: '', // the search type

    // The filters that will be enabled the next time
    // the search is executed. Upon completion of a
    // search execution, these filters should be set
    // to match the execution filters. Expected format
    // is string key matching Filter label and value
    // array of strings specifying the enabled Filter values.
    // E.g. { 'Brands': ['CB2', 'West Elm'] }
    enabledFilters: {},

    // The search status.
    // One of: Running | Complete | Error | LoadingNextPage
    status: 'Complete',

    // When search status is Error,
    // This field contains error information.
    error: null,
    enabledSort: null
  }

  /** Default constructs a Search with optional overrides. */
  constructor (overrides) {
    this.fields = { ...SearchBase.defaultProps, ...overrides };
  }

  /** Creates a new Search copy with optional overrides. */
  makeCopy (overrides) {
    return new SearchBase({ ...this.fields, ...overrides });
  }

  /**
   * Returns the search name.
   * @returns {string} name
   */
  name () {
    return this.fields.name;
  }

  /**
   * Returns the search type.
   * @returns {string} type
   */
  type () {
    return this.fields.type;
  }

  /**
   * Returns the enabled filters for this search.
   */
  enabledFilters () {
    return this.fields.enabledFilters;
  }

  /**
   * Returns the enabled sort for this search.
   */
  enabledSort () {
    return this.fields.enabledSort;
  }

  /**
   * Returns whether or not the search is currently running.
   * @returns {boolean} isRunning
   */
  isRunning () {
    return this.fields.status === 'Running';
  }

  /**
   * Returns whether or not the search is currently loading
   * the next page of results
   * @returns {boolean} isLoading
   */
  isLoadingNextPage () {
    return this.fields.status === 'LoadingNextPage';
  }

  /**
   * Returns whether or not the search is currently loading
   * the previous page of results
   * @returns {boolean} isLoading
   */
  isLoadingPreviousPage () {
    return this.fields.status === 'LoadingPreviousPage';
  }

  /**
   * Returns whether or not the last search run errored out.
   * @returns {boolean} hasError
   */
  hasError () {
    return this.fields.status === 'Error';
  }

  /**
   * Returns error information.
   * Only valid when hasError() is true.
   * @returns {Object} error
   */
  error () {
    return this.fields.error;
  }

  /**
   * Derived class should implement this.
   *
   * Returns whether or not the search is async.
   */
  isAsync () {
    console.error(`isAsync() not implemented for search type ${this.fields.type}`);
    return false;
  }

  /**
   * Derived class should implement this.
   *
   * Returns the dynamic filters available based
   * on the products in the current design.
   * @param {Object} state - The redux state object.
   * @returns {Filter[]} filters
   */
  availableFilters (state) {
    console.error(`availableFilters() not implemented for search type ${this.fields.type}`);
    return [];
  }

  /**
   * Derived class should implement this.
   *
   * Returns the dynamic sorts available based
   * on the products in the current design.
   * @param {Object} state - The redux state object.
   * @returns {Filter[]} filters
   */
  availableSorts (state) {
    console.error(`availableSorts() not implemented for search type ${this.fields.type}`);
    return [];
  }

  /**
   * Derived class should implement this.
   *
   * Returns the matching products from
   * the last time the search was executed.
   */
  products () {
    console.error(`products() not implemented for search type ${this.fields.type}`);
    return [];
  }

  /**
   * Derived class should implement this.
   *
   * Runs search query generating
   * search results.
   */
  runQuery () {
    console.error(`runQuery() not implemented for search type ${this.fields.type}`);
    return null;
  }

  /**
   * Derived class should override this if supporting pagination.
   *
   * @returns {boolean} paginatedResults
   */
  hasPagination () {
    return false;
  }

  /**
   * Derived class should override this if supporting pagination.
   *
   * @returns {boolean}
   */
  hasNextPage () {
    return false;
  }

  /**
   * Derived class should override this if supporting pagination.
   *
   * For paginated searches, loads the next page of results.
   */
  loadNextPage () {
    return null;
  }

  /**
   * Derived class should override this if supporting pagination.
   *
   * @returns {boolean}
   */
  hasPreviousPage () {
    return false;
  }

  /**
   * Derived class should override this if supporting pagination.
   *
   * For paginated searches, loads the previous page of results.
   */
  loadPreviousPage () {
    return null;
  }

  /**
   * Derived class should override this if supporting caching.
   *
   * @returns {boolean} supportsCaching
   */
  hasCaching () {
    return false;
  }

  /**
   * Derived class should override this if supporting caching.
   *
   * @returns {Object} cache
   * @returns {boolean} cache.hit - True if search result is found in cache
   * @returns {searchResults|null} cache.searchResults - The search results if cache.hit is true; otherwise, null.
   */
  static cacheMiss = { hit: false, searchResults: null };
  checkCache () {
    return SearchBase.cacheMiss;
  }

  /**
   * Clears any cached results
   */
  clearCache () {
    SearchBase.cacheMiss = { hit: false, searchResults: null };
  }
}
